import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { AddButton, AddCircleBlue, CustomDelete, EditWithIcon, ManageActions, MinLoader, NoItemsYet } from 'components';
import { OptionsChooseFromLibrary } from '../../../../../fragments';
import { renderOptionField, timeTypeEnums } from '../../../../../fragments/client/clientSingle/clientClinical/goals/targets/constants';
import {
    Colors,
    convertSecondToMinute,
    FindErrorItem,
    FindLoad,
    FindSuccessItem,
    goalDataTypeEnums,
    Images,
    isNotEmpty,
    useModal,
    PermissionList,
    RolePermission,
} from 'utils';
import { libraryActions, httpRequestsOnErrorsActions, httpRequestsOnSuccessActions } from 'store';
import { Svg } from 'assets';

export const LibraryGoalOption = () => {
    const { goalById } = useSelector((state) => ({
        goalById: state.library.goalById,
    }));
    const [optionRow, setOptionRow] = useState(null);
    const [inputs, setInputs] = useState({});
    const [error, setError] = useState('');
    const [options, setOptions] = useState([]);
    const OPTION_ACTION_TYPE = inputs?.id ? 'EDIT_LIBRARY_GOAL_OPTION' : 'CREATE_LIBRARY_GOAL_OPTION';
    const loadAdd = FindLoad(OPTION_ACTION_TYPE);
    const successCreate = FindSuccessItem(OPTION_ACTION_TYPE);
    const dispatch = useDispatch();
    const backError = FindErrorItem(OPTION_ACTION_TYPE);
    const { open } = useModal();
    const hasPermission = RolePermission([PermissionList.GOAL_CREATE?.code]);

    useEffect(() => {
        if (goalById?.options?.length) {
            setOptions(goalById.options);
        } else {
            setOptions([]);
        }
    }, [goalById]);

    useEffect(() => {
        if (successCreate) {
            resetCreation();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(OPTION_ACTION_TYPE));
        }
    }, [successCreate]);

    const resetCreation = () => {
        setOptionRow(null);
        setError('');
        setInputs({ value: '0' });
        dispatch(httpRequestsOnErrorsActions.removeError(OPTION_ACTION_TYPE));
    };
    const handleSave = () => {
        if (isNotEmpty(inputs?.label) && (inputs?.value || inputs?.value === '0' || inputs?.value === 0)) {
            const params = {
                label: inputs?.label,
                value: +inputs?.value,
            };

            if (inputs?.timeType) {
                params.timeType = inputs?.timeType;
                params.value = convertSecondToMinute(inputs?.timeType, inputs?.value);
            }

            if (inputs?.id) {
                dispatch(libraryActions.editLibraryGoalOption(goalById?.id, inputs?.id, params));
            } else {
                dispatch(libraryActions.createLibraryGoalOption(goalById?.id, params));
            }
        } else {
            setError(!isNotEmpty(inputs?.label) ? 'label' : !inputs?.value ? 'value' : '');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (value === '0' && goalById?.dataType !== goalDataTypeEnums?.PERCENT && goalById?.dataType !== goalDataTypeEnums?.RATING) {
            return;
        }

        setInputs((prev) => ({ ...prev, [name]: value }));

        if (error === name) {
            setError('');
        }
    };

    const handleDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination || source.index === destination.index) return;
        const updatedOptions = Array.from(options); // your state copy
        const [movedItem] = updatedOptions.splice(source.index, 1);
        updatedOptions.splice(destination.index, 0, movedItem);
        setOptions(updatedOptions);

        // const updatedGoal = {
        //     ...goalById,
        //     options: updatedOptions,
        // };

        dispatch(
            libraryActions.reorderLibraryGoalOption(goalById?.id, {
                indexFrom: source.index,
                indexTo: destination.index,
            })
        );
    };

    const addOption = (closePopover) => {
        closePopover?.();

        setOptionRow('new');
        if (goalById?.dataType === goalDataTypeEnums?.PERCENT) {
            setInputs({ value: '1' });
        }
    };
    const handleEditRow = (option) => {
        setOptionRow(option?.id);
        const params = {
            id: option.id,
            label: option.label,
            value: option.value + '',
        };

        option?.timeType ? (params.timeType = option?.timeType) : delete params.timeType;

        if (option?.timeType === timeTypeEnums?.MINUTE) {
            params.value = option?.value / 60;
        } else {
            params.value = option?.value;
        }
        setInputs(params);
        setError('');
    };

    return (
        <div>
            <p className="target-options-title">Options</p>
            <div
                className="target-options-wrapper"
                style={{
                    background: options.length ? '#F2F4F8' : 'transparent',
                    padding: options.length ? '16px' : '0',
                }}
            >
                {options?.length > 0 ? (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="optionList">
                            {(provided) => (
                                <div className="target-options-body" {...provided.droppableProps} ref={provided.innerRef}>
                                    {options.map((option, index) => (
                                        <Draggable key={option.id} draggableId={option.id} index={index} isDragDisabled={!hasPermission}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                    }}
                                                >
                                                    {optionRow === option?.id ? (
                                                        <div className="target-options-row" style={{ gap: 10 }}>
                                                            {goalById?.dataType === goalDataTypeEnums?.LATENCY ||
                                                            goalById?.dataType === goalDataTypeEnums?.WHOLE_INTERVAL ||
                                                            goalById?.dataType === goalDataTypeEnums?.PARTIAL_INTERVAL ||
                                                            goalById?.dataType === goalDataTypeEnums?.MOMENTARY_TIME_SAMPLING ? (
                                                                <p className="option-label-box">{inputs?.label}</p>
                                                            ) : (
                                                                <input
                                                                    value={inputs?.label || ''}
                                                                    onChange={handleChange}
                                                                    placeholder="Write Option Name"
                                                                    className="target-option-input"
                                                                    type="text"
                                                                    name="label"
                                                                    style={{
                                                                        borderColor: backError?.error
                                                                            ? '#FE7070'
                                                                            : error === 'label'
                                                                              ? '#FE7070'
                                                                              : undefined,
                                                                    }}
                                                                />
                                                            )}
                                                            <div className="flex-align-center" style={{ gap: 16 }}>
                                                                {renderOptionField(goalById, inputs, handleChange, error)}
                                                                <div className="flex-align-center" style={{ gap: 16 }}>
                                                                    <button onClick={handleSave} className="save-option-btn">
                                                                        {loadAdd?.length ? (
                                                                            <MinLoader
                                                                                margin="0"
                                                                                color={Colors.ThemeBlue}
                                                                                position="relative"
                                                                                width={13}
                                                                                height={13}
                                                                            />
                                                                        ) : (
                                                                            <img src={Images.checkmarkBlue} alt="icon" />
                                                                        )}
                                                                        <p>Save</p>
                                                                    </button>
                                                                    <button onClick={resetCreation} className="cancel-option-btn">
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className="target-options-row"
                                                            style={{
                                                                background: snapshot.isDragging ? '#f0f0f0' : 'white',
                                                                gap: 10,
                                                            }}
                                                        >
                                                            <p className="option-label-box">{option?.label || '—'}</p>

                                                            <div className="flex-align-center" style={{ gap: 16 }}>
                                                                {renderOptionField(goalById, option)}
                                                                <div className="flex-align-center" style={{ gap: 16 }}>
                                                                    {hasPermission && <EditWithIcon
                                                                        icon={Images.edit}
                                                                        type="button"
                                                                        handleClick={() => handleEditRow(option)}
                                                                    />}

                                                                    {hasPermission && <EditWithIcon
                                                                        icon={Images.redTrash}
                                                                        type="button"
                                                                        handleClick={() =>
                                                                            open(
                                                                                <CustomDelete
                                                                                    text="Are you sure you want to delete this option? This action cannot be undone."
                                                                                    info="Delete Option"
                                                                                    handleDel={() =>
                                                                                        dispatch(
                                                                                            libraryActions.deleteLibraryGoalOption(
                                                                                                goalById?.id,
                                                                                                option?.id
                                                                                            )
                                                                                        )
                                                                                    }
                                                                                    actionType={'DELETE_LIBRARY_GOAL_OPTION'}
                                                                                    innerText={'Delete'}
                                                                                />
                                                                            )
                                                                        }
                                                                    />}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                ) : (
                    <div style={{ background: '#F2F4F8', borderRadius: 8 }}>
                        <NoItemsYet
                            height={'250px'}
                            text={'You don’t have Option yet'}
                            subTitle={'Nothing new for now. Enjoy the silence while it lasts'}
                            image={Images.noClientYet}
                            containerStyles={{ position: 'relative' }}
                            renderButton={() => hasPermission ? (
                                <ManageActions
                                    btnClass="reset-btn"
                                    renderBtnView={() => (
                                        <div style={{ width: 'fit-content', marginTop: 24 }}>
                                            <AddButton styles={{ width: 'fit-content' }} text={'Add Option'} />
                                        </div>
                                    )}
                                    renderBody={(closePopover) => (
                                        <div className="flex" style={{ flexDirection: 'column' }}>
                                            <button className="header-add-goals-items-btn" onClick={() => addOption(closePopover)}>
                                                Add new option
                                            </button>

                                            <button
                                                className="header-add-goals-items-btn"
                                                onClick={() => {
                                                    open(
                                                        <OptionsChooseFromLibrary
                                                            CHOOSE_ACTION_TYPE={'CHOOSE_OPTIONS_FROM_LIBRARY_GOAL'}
                                                            action={libraryActions.chooseOptionsFromLibraryGoal}
                                                            goalById={goalById}
                                                            fromTheLibrary
                                                        />,
                                                        {
                                                            styles: {
                                                                wrapperStyle: {
                                                                    width: 'calc(100vw - 32px)',
                                                                    maxWidth: '1370px',
                                                                },
                                                            },
                                                        }
                                                    );
                                                    closePopover?.();
                                                }}
                                            >
                                                Choose from Library
                                            </button>
                                        </div>
                                    )}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    styleBodyItems={{ padding: 0 }}
                                />
                            ) : null}
                        />
                    </div>
                )}

                {optionRow === 'new' && (
                    <div>
                        <div className="target-options-row" style={{ gap: 10 }}>
                            {goalById?.dataType === goalDataTypeEnums?.LATENCY ||
                            goalById?.dataType === goalDataTypeEnums?.WHOLE_INTERVAL ||
                            goalById?.dataType === goalDataTypeEnums?.PARTIAL_INTERVAL ||
                            goalById?.dataType === goalDataTypeEnums?.MOMENTARY_TIME_SAMPLING ? (
                                <p className="option-label-box">{inputs?.label}</p>
                            ) : (
                                <input
                                    value={inputs?.label || ''}
                                    onChange={handleChange}
                                    placeholder="Write Option Name"
                                    className="target-option-input"
                                    type="text"
                                    name="label"
                                    style={{
                                        borderColor: backError?.error ? '#FE7070' : error === 'label' ? '#FE7070' : undefined,
                                    }}
                                />
                            )}
                            <div className="flex-align-center" style={{ gap: 16 }}>
                                {renderOptionField(goalById, inputs, handleChange, error)}

                                <div className="flex-align-center" style={{ gap: 16 }}>
                                    <button onClick={handleSave} className="save-option-btn">
                                        {loadAdd?.length ? (
                                            <MinLoader margin="0" color={Colors.ThemeBlue} position="relative" width={13} height={13} />
                                        ) : (
                                            <img src={Images.checkmarkBlue} alt="icon" />
                                        )}
                                        <p>Add</p>
                                    </button>
                                    <button onClick={resetCreation} className="cancel-option-btn">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!optionRow &&
                    !!options?.length &&
                    hasPermission &&
                    goalById?.dataType !== goalDataTypeEnums?.LATENCY &&
                    goalById?.dataType !== goalDataTypeEnums?.WHOLE_INTERVAL &&
                    goalById?.dataType !== goalDataTypeEnums?.PARTIAL_INTERVAL &&
                    goalById?.dataType !== goalDataTypeEnums?.MOMENTARY_TIME_SAMPLING && (
                        <div className="flex-end" style={{ margin: '8px 0 0' }}>
                            <ManageActions
                                btnClass="reset-btn"
                                renderBtnView={(isOpen) => (
                                    <div className="flex-align-center" style={{ gap: 8 }}>
                                        <AddCircleBlue text={'Add Option'} />

                                        <Svg.ArrowRight2Svg
                                            style={{
                                                transform: isOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
                                                filter: 'brightness(0) saturate(100%) invert(29%) sepia(94%) saturate(1578%) hue-rotate(201deg) brightness(90%) contrast(100%)',
                                            }}
                                        />
                                    </div>
                                )}
                                renderBody={(closePopover) => (
                                    <div className="flex" style={{ flexDirection: 'column' }}>
                                        <button className="header-add-goals-items-btn" onClick={() => addOption(closePopover)}>
                                            Add new option
                                        </button>

                                        <button
                                            className="header-add-goals-items-btn"
                                            onClick={() => {
                                                open(
                                                    <OptionsChooseFromLibrary
                                                        CHOOSE_ACTION_TYPE={'CHOOSE_OPTIONS_FROM_LIBRARY_GOAL'}
                                                        action={libraryActions.chooseOptionsFromLibraryGoal}
                                                        goalById={goalById}
                                                        fromTheLibrary
                                                    />,
                                                    {
                                                        styles: {
                                                            wrapperStyle: {
                                                                width: 'calc(100vw - 32px)',
                                                                maxWidth: '1370px',
                                                            },
                                                        },
                                                    }
                                                );
                                                closePopover?.();
                                            }}
                                        >
                                            Choose from Library
                                        </button>
                                    </div>
                                )}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                styleBodyItems={{ padding: 0 }}
                            />
                        </div>
                    )}
            </div>
        </div>
    );
};
